import { useEffect, useRef, useState } from 'react';
import api from '../services/api';

// Fields the identify endpoint can return that map onto the add-card form.
export const IDENTIFY_FIELDS = [
  'player_name', 'year', 'brand', 'set_name', 'card_number', 'sport',
  // Only ever set from a grading slab's printed label -- the model is told
  // to leave these empty for raw cards in sleeves/toploaders.
  'grade', 'grading_company',
];

const hasValue = (v) => v !== null && v !== undefined && v !== '' && v !== false;

// Copy identified values into the form's blank fields. The user's input wins.
export function fillBlankFields(prev, card) {
  const next = { ...prev };
  IDENTIFY_FIELDS.forEach(field => {
    if (!hasValue(card[field])) return;
    if (String(prev[field] ?? '').trim() !== '') return;
    next[field] = String(card[field]);
  });
  return next;
}

/**
 * Reads a card's details off its photos so an add-card form can pre-fill.
 * Fires on its own once the front photo settles, so the user only has to
 * pick images.
 *
 * Reads at most twice per card, to keep the paid API calls down:
 *  - once when the front settles (with the back too, if already added)
 *  - once more only if that read wasn't confident, didn't have the back, and
 *    the back is then added -- the back carries the card number and year
 * Cropping or re-adding photos otherwise never re-reads. Removing the front
 * starts over; a failed read retries on the next photo change; hitting the
 * server's daily cap stops reading until the form is reset.
 *
 * Rules this must not break:
 *  - never block submission: any failure leaves the manual form working
 *  - never fire while a crop is open (`paused`); the cropped image reads better
 *  - never fire when editing an existing card (`disabled`)
 *
 * `onResult(card)` receives the identified fields; the caller decides how to
 * apply them (usually `setForm(prev => fillBlankFields(prev, card))`).
 */
export function useCardAutoIdentify({ front, back, paused = false, disabled = false, onResult }) {
  const [identifying, setIdentifying] = useState(false);
  const [rereading, setRereading] = useState(false);
  const [note, setNote] = useState('');
  // Bumped after each successful read so the effect re-evaluates -- a back
  // added while the first read was in flight still gets its re-read.
  const [readTick, setReadTick] = useState(0);
  const firstReadRef = useRef(null); // { hadBack, confident } once read
  const rereadDoneRef = useRef(false);
  const inFlightRef = useRef(false);
  const limitReachedRef = useRef(false);
  // Bumped when the front is cleared, so a read still in flight for the old
  // card is ignored. Nothing else abandons a read: cropping or adding the back
  // mid-request must not throw away a paid result.
  const generationRef = useRef(0);
  const onResultRef = useRef(onResult);
  onResultRef.current = onResult;

  useEffect(() => {
    // No photo means the photo was removed or the form reset -- start fresh.
    if (!front) {
      firstReadRef.current = null;
      rereadDoneRef.current = false;
      inFlightRef.current = false;
      limitReachedRef.current = false;
      generationRef.current += 1;
      setIdentifying(false);
      setNote('');
      return undefined;
    }
    if (disabled || paused || inFlightRef.current || limitReachedRef.current) return undefined;

    const first = firstReadRef.current;
    const isReread = !!first;
    if (isReread && (!back || first.hadBack || first.confident || rereadDoneRef.current)) {
      return undefined;
    }

    // Short delay so a forced crop that opens right after picking can pause
    // this before any call is made.
    const timer = setTimeout(async () => {
      inFlightRef.current = true;
      setRereading(isReread);
      const generation = generationRef.current;
      const stale = () => generation !== generationRef.current;
      setIdentifying(true);
      setNote('');
      try {
        const body = new FormData();
        body.append('front_image', front);
        if (back) body.append('back_image', back);
        const res = await api.post('/cards/my-cards/identify/', body, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        if (stale()) return;

        if (!res.data?.success) {
          if (res.data?.limit_reached) limitReachedRef.current = true;
          setNote(res.data?.error || '');
          return;
        }

        const card = res.data.card || {};
        const confident = !card.confidence || card.confidence === 'high';
        if (isReread) rereadDoneRef.current = true;
        else firstReadRef.current = { hadBack: !!back, confident };

        const filledAnything = IDENTIFY_FIELDS.some(f => hasValue(card[f]));
        onResultRef.current?.(card);

        if (!confident) {
          setNote(
            card.unreadable_reason
              ? `Double-check these. ${card.unreadable_reason}`
              : 'Double-check these. Some details were hard to read.'
          );
        } else if (filledAnything) {
          setNote('Filled in from your photos. Edit anything that looks wrong.');
        }
        setReadTick(t => t + 1);
      } catch (err) {
        if (!stale()) setNote('');
      } finally {
        if (!stale()) {
          inFlightRef.current = false;
          setIdentifying(false);
        }
      }
    }, 1500);

    return () => clearTimeout(timer);
  }, [front, back, paused, disabled, readTick]);

  return { identifying, rereading, note };
}
